import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '../layouts/PageLayout';
import { SEOHead } from '../components/seo/SEOHead';
import { GlassPanel } from '../components/ui/GlassPanel';
import { Badge } from '../components/ui/Badge';
import { parseFrontmatter } from '../lib/frontmatter';
import { SITE } from '../utils/seo';

const ARTICLE_FILES = ['example-article'];

function useArticleList() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all(
      ARTICLE_FILES.map((slug) =>
        fetch(`/content/articles/${slug}.md`)
          .then((r) => r.text())
          .then((text) => {
            const { frontmatter } = parseFrontmatter(text);
            return { slug, ...frontmatter };
          })
          .catch(() => null)
      )
    ).then((results) => {
      setArticles(results.filter(Boolean).sort((a, b) => (b.date || '').localeCompare(a.date || '')));
      setLoading(false);
    });
  }, []);

  return { articles, loading };
}

export function Articles() {
  const { articles, loading } = useArticleList();

  return (
    <PageLayout>
      <SEOHead
        title="Articles"
        description="Notes on technology, data engineering, and systems by Simone Carani."
        canonical={`${SITE.url}/articles`}
      />

      <header className="mb-10">
        <h1 className="font-serif text-4xl text-[var(--color-text)] mb-2">Articles</h1>
        <p className="text-[var(--color-muted)]">Notes on tech, data, and systems thinking.</p>
      </header>

      {loading && <p className="text-[var(--color-muted)] animate-pulse">Loading…</p>}

      <ul className="flex flex-col gap-4">
        {articles.map((article) => (
          <li key={article.slug}>
            <GlassPanel
              as={Link}
              to={`/articles/${article.slug}`}
              className="group block p-5 hover:bg-white/[0.06] hover:border-[var(--color-accent)]/20 transition-all no-underline"
              aria-label={`Read article: ${article.title}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {article.date && (
                    <time
                      dateTime={article.date}
                      className="text-xs text-[var(--color-muted)]/60 font-mono tracking-wide"
                    >
                      {article.date}
                    </time>
                  )}
                  <h2 className="text-base font-semibold text-[var(--color-text)] group-hover:text-[var(--color-accent)] transition-colors mt-0.5">
                    {article.title}
                  </h2>
                  {article.description && (
                    <p className="text-sm text-[var(--color-muted)] mt-1 line-clamp-2">
                      {article.description}
                    </p>
                  )}
                  {Array.isArray(article.tags) && article.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {article.tags.map((tag) => (
                        <Badge key={tag}>{tag}</Badge>
                      ))}
                    </div>
                  )}
                </div>
                <span aria-hidden="true" className="text-[var(--color-muted)]/40 group-hover:text-[var(--color-accent)] transition-colors mt-0.5 flex-shrink-0">
                  →
                </span>
              </div>
            </GlassPanel>
          </li>
        ))}
      </ul>
    </PageLayout>
  );
}
